/* Display only: never publishes inventory, creates variants, or unlocks checkout. */
(() => {
  'use strict';
  const zone = 'America/Los_Angeles';
  function formatDates(root) {
    root.querySelectorAll('[data-hl-date]').forEach(el => {
      const date = new Date(el.dataset.hlDate);
      if (!Number.isFinite(date.getTime())) return;
      const compact = el.dataset.hlDateStyle === 'badge';
      const day = new Intl.DateTimeFormat('en-US', {timeZone:zone, month:compact?'short':'long', day:'numeric', ...(compact?{}:{year:'numeric'})}).format(date);
      const time = new Intl.DateTimeFormat('en-US', {timeZone:zone, hour:'numeric', ...(date.getUTCMinutes()?{minute:'2-digit'}:{})}).format(date);
      el.textContent = compact ? `AVAILABLE ${day.toUpperCase()} · ${time} PT` : `${day} · ${time} Pacific Time`;
    });
  }
  class DropGallery extends HTMLElement {
    connectedCallback() {
      if(this.running) return;
      this.running = true;
      this.controller = new AbortController();
      this.adoptClock(this);
      this.serverMarkup=this.querySelector('[data-hl-refresh-region]')?.innerHTML;
      formatDates(this);
      this.onClick = e => { if(e.target.closest('[data-hl-refresh]')) this.refresh(true); };
      this.onVisible = () => { if(!document.hidden) this.refresh(); };
      this.addEventListener('click', this.onClick);
      document.addEventListener('visibilitychange', this.onVisible);
      this.timer = setInterval(() => this.tick(), 1000);
      this.tick();
      // Correct cached server timestamps before trusting the visible countdown.
      this.refresh();
    }
    disconnectedCallback() {
      this.running = false; clearInterval(this.timer); this.controller?.abort();
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('visibilitychange', this.onVisible);
    }
    adoptClock(source, httpDate) {
      const fromHeader = Date.parse(httpDate || '');
      const fromLiquid = Number(source.dataset.serverNow) * 1000;
      this.serverTime = Number.isFinite(fromHeader) ? fromHeader : (fromLiquid > 0 ? fromLiquid : NaN);
      this.monotonicStart = performance.now();
    }
    tick() {
      const now = this.serverTime + performance.now() - this.monotonicStart;
      let boundary = false;
      this.querySelectorAll('[data-hl-release]').forEach(release => {
        const end = Date.parse(release.dataset.releaseAt);
        const clock = release.querySelector('[data-hl-clock]');
        if(!Number.isFinite(end) || !Number.isFinite(now)) return;
        if(end<=now && release.dataset.mode==='scheduled') boundary=true;
        if(!clock) return;
        const seconds = Math.max(0, Math.ceil((end-now)/1000));
        const units = {days:Math.floor(seconds/86400),hours:Math.floor(seconds/3600)%24,minutes:Math.floor(seconds/60)%60,seconds:seconds%60};
        Object.entries(units).forEach(([key,value]) => { const el=clock.querySelector(`[data-hl-unit="${key}"]`); if(el) el.textContent=String(value).padStart(2,'0'); });
        if(seconds===0 && release.dataset.mode==='scheduled') boundary = true;
      });
      if(document.hidden || window.Shopify?.designMode) return;
      const interval = boundary || this.querySelector('[data-state="releasing"]') ? 5000 : 30000;
      if(!this.lastRefresh || performance.now()-this.lastRefresh >= interval) this.refresh();
    }
    async refresh(manual=false) {
      if(this.loading || !this.running) return;
      this.loading=true; this.lastRefresh=performance.now();
      const notice=this.querySelector('[data-hl-sync]');
      const url=new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id',this.dataset.sectionId);
      url.searchParams.set('_hl_drop_refresh',String(Math.floor(performance.now())));
      try {
        const response=await fetch(url,{cache:'no-store',credentials:'same-origin',signal:this.controller.signal});
        if(!response.ok) throw new Error('Unavailable');
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const fresh=Array.from(doc.querySelectorAll('hl-drop-gallery')).find(el=>el.dataset.sectionId===this.dataset.sectionId);
        const region=this.querySelector('[data-hl-refresh-region]');
        const next=fresh?.querySelector('[data-hl-refresh-region]');
        if(!fresh || !region || !next) throw new Error('Invalid section');
        this.adoptClock(fresh,response.headers.get('Date'));
        // Avoid replacing links while a visitor has keyboard focus inside the gallery.
        if(this.serverMarkup!==next.innerHTML && !region.contains(document.activeElement)) { region.innerHTML=next.innerHTML; this.serverMarkup=next.innerHTML; }
        formatDates(this);
        this.tick();
        if(notice) notice.textContent=this.querySelector('[data-state="releasing"]')?'The release is opening. Availability updates automatically.':manual?'Availability updated.':'';
      } catch(error) {
        if(error.name!=='AbortError' && notice) notice.textContent='Availability could not be refreshed. Please try again.';
      } finally { this.loading=false; }
    }
  }
  class DropProductStatus extends HTMLElement {
    connectedCallback() {
      formatDates(this);
      if(this.timer) return;
      this.controller = new AbortController();
      this.timer=setInterval(()=>this.refresh(),20000);
    }
    disconnectedCallback() {clearInterval(this.timer);this.timer=null;this.controller?.abort();}
    async refresh() {
      if(document.hidden || this.loading || !this.dataset.productUrl) return;
      this.loading=true;
      try {
        const url=new URL(this.dataset.productUrl+'.js',window.location.origin);
        const response=await fetch(url,{cache:'no-store',credentials:'same-origin',signal:this.controller.signal});
        if(!response.ok) return;
        const product=await response.json();
        const label=this.querySelector('[data-hl-stock]');
        if(label) label.textContent=product.available?'AVAILABLE NOW':'COLLECTED';

      } catch(_) { /* Shopify remains the authority; keep last confirmed state. */ }
      finally {this.loading=false;}
    }
  }
  formatDates(document);
  const gate=document.querySelector('[data-hl-product-gate]');
  if(gate && !window.Shopify?.designMode) {
    let checking=false;
    setInterval(async()=>{
      if(document.hidden || checking) return;
      checking=true;
      try {
        const url=new URL(location.href);
        url.searchParams.set('section_id',gate.dataset.hlSection);
        url.searchParams.set('_hl_gate',Date.now());
        const response=await fetch(url,{cache:'no-store',credentials:'same-origin'});
        if(!response.ok) return;
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');
        const next=doc.querySelector('[data-hl-product-gate]');
        if(!next && doc.querySelector('.main-product--root') || next && next.dataset.hlProductGate!==gate.dataset.hlProductGate) location.reload();
      } catch (_) {} finally {checking=false;}
    },15000);
  }
  if(!customElements.get('hl-drop-gallery')) customElements.define('hl-drop-gallery',DropGallery);
  if(!customElements.get('hl-drop-product-status')) customElements.define('hl-drop-product-status',DropProductStatus);
})();
